import pdfMake from 'pdfmake/build/pdfmake'
// @ts-ignore
import vfs from 'pdfmake/build/vfs_fonts.js'

// Garante as fontes (vfs) na instância usada.
// @ts-ignore
;(pdfMake as any).vfs = (vfs && vfs.pdfMake && vfs.pdfMake.vfs) ? vfs.pdfMake.vfs : (vfs as any).vfs

export interface ColunaPdf {
	text: string
	value: string
	align?: string
}

export interface SecaoPdf {
	titulo: string
	headers: ColunaPdf[]
	itens: any[]
}

export interface DashboardPdfParams {
	corPrimaria: string
	empresa: string
	periodoLabel: string
	lojasLabel: string
	geradoEm: string
	kpis: { titulo: string; valor: string }[]
	secoes: SecaoPdf[]
}

export function carregarImagem(url: string): Promise<string | null> {
	return fetch(url)
		.then(r => r.blob())
		.then(blob => new Promise<string | null>(resolve => {
			const reader = new FileReader()
			reader.onloadend = () => resolve(reader.result as string)
			reader.onerror = () => resolve(null)
			reader.readAsDataURL(blob)
		}))
		.catch(() => null)
}

export function clarear(hex: string): string {
	// fundo bem claro derivado da cor da marca, para os KPI cards
	const h = hex.replace('#', '')
	if (h.length !== 6) return '#F4F3F8'
	const r = parseInt(h.slice(0, 2), 16)
	const g = parseInt(h.slice(2, 4), 16)
	const b = parseInt(h.slice(4, 6), 16)
	const mix = (c: number) => Math.round(c + (255 - c) * 0.92)
	return `#${[mix(r), mix(g), mix(b)].map(c => c.toString(16).padStart(2, '0')).join('')}`
}

function kpiCard(kpi: { titulo: string; valor: string }, cor: string, fundo: string) {
	return {
		table: {
			widths: ['*'],
			body: [[
				{
					margin: [8, 8, 8, 8],
					stack: [
						{ text: kpi.titulo.toUpperCase(), fontSize: 7, color: '#8A8A8A', characterSpacing: 0.5 },
						{ text: kpi.valor, fontSize: 14, bold: true, color: cor, margin: [0, 3, 0, 0] },
					],
				},
			]],
		},
		layout: {
			fillColor: () => fundo,
			hLineWidth: (i: number) => (i === 0 ? 3 : 0),
			vLineWidth: () => 0,
			hLineColor: () => cor,
			paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0,
		},
	}
}

function tabelaDaSecao(secao: SecaoPdf, cor: string) {
	const cabecalho = secao.headers.map(h => ({
		text: h.text, bold: true, fontSize: 8, color: 'white', fillColor: cor,
		alignment: h.align === 'end' ? 'right' : 'left', margin: [0, 3, 0, 3],
	}))
	const linhas = secao.itens.map(item =>
		secao.headers.map(h => ({
			text: String(item[h.value] ?? ''), fontSize: 8,
			alignment: h.align === 'end' ? 'right' : 'left', margin: [0, 2, 0, 2],
		})),
	)
	return [
		{
			columns: [
				{ canvas: [{ type: 'rect', x: 0, y: 2, w: 3, h: 12, color: cor }], width: 8 },
				{ text: secao.titulo, fontSize: 12, bold: true, color: '#333', margin: [0, 0, 0, 0] },
			],
			margin: [0, 14, 0, 5],
		},
		{
			table: {
				headerRows: 1,
				widths: secao.headers.map((_, i) => (i === 0 ? '*' : 'auto')),
				body: [cabecalho, ...linhas],
			},
			layout: {
				hLineWidth: () => 0.5,
				vLineWidth: () => 0,
				hLineColor: () => '#E4E2EE',
				fillColor: (rowIndex: number) => (rowIndex > 0 && rowIndex % 2 === 0 ? '#F8F7FC' : null),
				paddingLeft: () => 6, paddingRight: () => 6,
			},
		},
	]
}

export async function gerarPdfDashboard(params: DashboardPdfParams) {
	const cor = params.corPrimaria || '#60269E'
	const fundoKpi = clarear(cor)
	const logo = await carregarImagem('/logo-almode.png')

	const cabecalhoEsq: any[] = []
	if (logo) cabecalhoEsq.push({ image: logo, width: 86, margin: [0, 4, 0, 0] })
	else cabecalhoEsq.push({ text: 'ALMODE', color: 'white', bold: true, fontSize: 16, characterSpacing: 3, margin: [0, 8, 0, 0] })

	const conteudo: any[] = [
		{
			table: {
				widths: ['auto', '*'],
				body: [[
					{ stack: cabecalhoEsq, fillColor: cor, margin: [14, 10, 10, 10], border: [false, false, false, false] },
					{
						fillColor: cor, margin: [10, 12, 14, 10], border: [false, false, false, false], alignment: 'right',
						stack: [
							{ text: 'Resumo do dashboard', color: 'white', bold: true, fontSize: 18 },
							{ text: params.empresa, color: 'white', fontSize: 10, margin: [0, 2, 0, 0], opacity: 0.9 },
						],
					},
				]],
			},
			layout: 'noBorders',
			margin: [0, 0, 0, 12],
		},
		{
			table: {
				widths: ['*'],
				body: [[
					{
						fillColor: fundoKpi, margin: [12, 8, 12, 8],
						columns: [
							{ text: [{ text: 'PERÍODO  ', color: cor, bold: true, fontSize: 8 }, { text: params.periodoLabel, fontSize: 9 }] },
							{ text: [{ text: 'LOJAS  ', color: cor, bold: true, fontSize: 8 }, { text: params.lojasLabel, fontSize: 9 }], alignment: 'center' },
							{ text: [{ text: 'GERADO EM  ', color: cor, bold: true, fontSize: 8 }, { text: params.geradoEm, fontSize: 9 }], alignment: 'right' },
						],
					},
				]],
			},
			layout: 'noBorders',
			margin: [0, 0, 0, 14],
		},
		{
			columns: params.kpis.map(k => kpiCard(k, cor, fundoKpi)),
			columnGap: 8,
			margin: [0, 0, 0, 4],
		},
	]

	params.secoes
		.filter(s => s.itens && s.itens.length)
		.forEach(s => conteudo.push(...tabelaDaSecao(s, cor)))

	const docDefinition: any = {
		pageSize: 'A4',
		pageOrientation: 'landscape',
		pageMargins: [28, 28, 28, 38],
		content: conteudo,
		defaultStyle: { fontSize: 9, color: '#333' },
		footer: (currentPage: number, pageCount: number) => ({
			columns: [
				{ text: 'Gerado pelo Almode', fontSize: 8, color: '#9A9A9A', margin: [28, 0, 0, 0] },
				{ text: `${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 8, color: '#9A9A9A', margin: [0, 0, 28, 0] },
			],
			margin: [0, 12, 0, 0],
		}),
	}

	pdfMake.createPdf(docDefinition).open()
}
